import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { ClipboardCheck, Search, Printer, Eye, Trash2, Loader2, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const resultColors = { conforme: "bg-green-500", nao_conforme: "bg-red-500", condicional: "bg-amber-500" };
const resultLabels = { conforme: "CONFORME", nao_conforme: "NÃO CONFORME", condicional: "CONDICIONAL" };
const resultIcons = { conforme: CheckCircle2, nao_conforme: XCircle, condicional: AlertTriangle };

function StatusBadge({ val }) {
  if (!val) return <Badge variant="outline" className="text-xs">N/A</Badge>;
  if (val === "OK") return <Badge className="bg-green-500 text-white text-xs">OK</Badge>;
  if (val === "NOK") return <Badge className="bg-red-500 text-white text-xs">NOK</Badge>;
  return <Badge variant="outline" className="text-xs">{val}</Badge>;
}

function InspectionDetail({ inspection, onClose }) {
  const i = inspection;
  const ResultIcon = resultIcons[i.resultado_global] || CheckCircle2;

  const handlePrint = () => {
    const logoUrl = `${process.env.REACT_APP_BACKEND_URL}/logo-ep3d.png`;
    const htmlContent = `<!DOCTYPE html><html><head><title>Inspeção ${i.order_code}</title>
    <style>
      @page{size:A4;margin:15mm}
      body{font-family:Arial,sans-serif;padding:0;color:#1e293b;font-size:12px;line-height:1.4}
      .header{display:flex;justify-content:space-between;align-items:center;border-bottom:3px solid #f59e0b;padding-bottom:10px;margin-bottom:20px}
      .header img{height:50px}
      h1{font-size:18px;color:#0f172a;margin:0 0 15px}
      h2{font-size:14px;margin-top:20px;margin-bottom:8px;color:#0f172a;border-bottom:1px solid #e2e8f0;padding-bottom:4px}
      table{width:100%;border-collapse:collapse;margin:8px 0}
      th{background:#0f172a;color:#f59e0b;padding:6px 10px;text-align:left;font-size:11px}
      td{padding:5px 10px;border-bottom:1px solid #e5e7eb}
      .ok{color:#16a34a;font-weight:bold} .nok{color:#dc2626;font-weight:bold} .na{color:#9ca3af}
      .result{text-align:center;padding:12px;font-size:18px;font-weight:bold;border-radius:8px;margin:15px 0}
      .conforme{background:#dcfce7;color:#166534;border:2px solid #22c55e}
      .nao_conforme{background:#fecaca;color:#991b1b;border:2px solid #ef4444}
      .condicional{background:#fef3c7;color:#92400e;border:2px solid #f59e0b}
      .info{display:flex;gap:20px;margin-bottom:15px;background:#f8fafc;padding:10px;border-radius:6px}
      .info div{flex:1}
      .footer{margin-top:30px;padding-top:10px;border-top:1px solid #e5e7eb;color:#9ca3af;font-size:10px;text-align:center}
    </style></head><body>
    <div class="header">
      <img src="${logoUrl}" alt="EP3D" onerror="this.outerHTML='<div style=\\'font-size:22px;font-weight:900;color:#0f172a\\'>EP3D Produção</div>'" />
      <div style="text-align:right;font-size:10px;color:#64748b;">Relatório de Inspeção<br><strong>Técnico de Qualidade</strong></div>
    </div>
    <h1>Inspeção — ${i.order_code}</h1>
    <div class="info">
      <div><strong>Ordem:</strong> ${i.order_code}</div>
      <div><strong>Máquina:</strong> ${i.machine_code}</div>
      <div><strong>Peça:</strong> ${i.part_code} - ${i.part_description}</div>
      <div><strong>Molde:</strong> ${i.mold_code}</div>
    </div>
    <div class="info">
      <div><strong>Inspector:</strong> ${i.inspector_name}</div>
      <div><strong>Data:</strong> ${new Date(i.created_at).toLocaleString("pt-PT")}</div>
    </div>
    <div class="result ${i.resultado_global}">${resultLabels[i.resultado_global] || i.resultado_global}</div>
    
    <h2>1. Inspeção Visual e Estética</h2>
    <table><tr><th>Ponto</th><th>Resultado</th></tr>
    ${Object.entries(i.visual || {}).map(([k,v]) => `<tr><td>${k.charAt(0).toUpperCase()+k.slice(1)}</td><td class="${v==="OK"?"ok":v==="NOK"?"nok":"na"}">${v||"N/A"}</td></tr>`).join("")}
    </table>
    
    <h2>2. Dimensões e Espessura</h2>
    <table><tr><th>Medida</th><th>Valor</th></tr>
    <tr><td>Espessura</td><td>${i.dimensions?.espessura ? i.dimensions.espessura+" mm" : "N/A"}</td></tr>
    <tr><td>Diâmetro</td><td>${i.dimensions?.diametro ? i.dimensions.diametro+" mm" : "N/A"}</td></tr>
    <tr><td>Comprimento</td><td>${i.dimensions?.comprimento ? i.dimensions.comprimento+" mm" : "N/A"}</td></tr>
    <tr><td>Instrumento</td><td>${i.dimensions?.instrumento || "N/A"}</td></tr>
    </table>
    
    <h2>3. Ensaios Mecânicos</h2>
    <table><tr><th>Ensaio</th><th>Resultado</th></tr>
    <tr><td>Resistência à Tração</td><td class="${i.mechanical?.tracao==="OK"?"ok":i.mechanical?.tracao==="NOK"?"nok":"na"}">${i.mechanical?.tracao||"N/A"}</td></tr>
    <tr><td>Resistência ao Impacto</td><td class="${i.mechanical?.impacto==="OK"?"ok":i.mechanical?.impacto==="NOK"?"nok":"na"}">${i.mechanical?.impacto||"N/A"}</td></tr>
    <tr><td>Estanqueidade</td><td class="${i.mechanical?.estanqueidade==="OK"?"ok":i.mechanical?.estanqueidade==="NOK"?"nok":"na"}">${i.mechanical?.estanqueidade||"N/A"}</td></tr>
    </table>
    
    <h2>4. Peso e Densidade</h2>
    <table><tr><th>Item</th><th>Valor</th></tr>
    <tr><td>Peso da Amostra</td><td>${i.weight?.valor ? i.weight.valor+" g" : "N/A"}</td></tr>
    <tr><td>Uniformidade</td><td class="${i.weight?.uniforme==="OK"?"ok":i.weight?.uniforme==="NOK"?"nok":"na"}">${i.weight?.uniforme||"N/A"}</td></tr>
    </table>
    
    <h2>5. Propriedades Químicas/Físicas</h2>
    <table><tr><th>Item</th><th>Valor</th></tr>
    <tr><td>Índice de Fluidez (MFI)</td><td>${i.chemical?.fluidez ? i.chemical.fluidez+" g/10min" : "N/A"}</td></tr>
    </table>
    
    ${i.observacoes ? `<h2>Observações</h2><p>${i.observacoes}</p>` : ""}
    
    <div class="footer">
      <p>EP3D Produção — Relatório de Inspeção de Qualidade</p>
      <p>Gerado em ${new Date().toLocaleString("pt-PT")}</p>
    </div>
    </body></html>`;
    
    // Download como ficheiro HTML (abre no browser e permite "Guardar como PDF")
    const blob = new Blob([htmlContent], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Inspecao_${i.order_code}_${new Date(i.created_at).toISOString().split("T")[0]}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <ClipboardCheck className="h-5 w-5 text-indigo-600" />
          Inspeção — {i.order_code}
        </DialogTitle>
      </DialogHeader>
      <div className="space-y-4 mt-2">
        {/* Header Info */}
        <div className="grid grid-cols-4 gap-3 text-sm">
          <div className="bg-slate-50 rounded p-2"><p className="text-xs text-slate-400">Ordem</p><p className="font-mono font-bold">{i.order_code}</p></div>
          <div className="bg-slate-50 rounded p-2"><p className="text-xs text-slate-400">Máquina</p><p className="font-bold">{i.machine_code}</p></div>
          <div className="bg-slate-50 rounded p-2"><p className="text-xs text-slate-400">Peça</p><p className="font-bold">{i.part_code}</p></div>
          <div className="bg-slate-50 rounded p-2"><p className="text-xs text-slate-400">Inspector</p><p className="font-bold">{i.inspector_name}</p></div>
        </div>

        {/* Result */}
        <div className={`${resultColors[i.resultado_global]} text-white text-center py-3 rounded-lg font-bold text-lg flex items-center justify-center gap-2`}>
          <ResultIcon className="h-6 w-6" />
          {resultLabels[i.resultado_global]}
        </div>

        {/* Visual */}
        <div className="border rounded-lg p-3">
          <p className="font-bold text-sm mb-2">1. Inspeção Visual</p>
          <div className="grid grid-cols-4 gap-2">
            {Object.entries(i.visual || {}).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between bg-slate-50 rounded px-2 py-1">
                <span className="text-xs capitalize">{k}</span>
                <StatusBadge val={v} />
              </div>
            ))}
          </div>
        </div>

        {/* Dimensions */}
        <div className="border rounded-lg p-3">
          <p className="font-bold text-sm mb-2">2. Dimensões</p>
          <div className="grid grid-cols-4 gap-2 text-sm">
            <div><span className="text-xs text-slate-400">Espessura</span><p className="font-mono">{i.dimensions?.espessura ? `${i.dimensions.espessura} mm` : "N/A"}</p></div>
            <div><span className="text-xs text-slate-400">Diâmetro</span><p className="font-mono">{i.dimensions?.diametro ? `${i.dimensions.diametro} mm` : "N/A"}</p></div>
            <div><span className="text-xs text-slate-400">Comprimento</span><p className="font-mono">{i.dimensions?.comprimento ? `${i.dimensions.comprimento} mm` : "N/A"}</p></div>
            <div><span className="text-xs text-slate-400">Instrumento</span><p>{i.dimensions?.instrumento || "N/A"}</p></div>
          </div>
        </div>

        {/* Mechanical */}
        <div className="border rounded-lg p-3">
          <p className="font-bold text-sm mb-2">3. Ensaios Mecânicos</p>
          <div className="grid grid-cols-3 gap-2">
            {[["tracao","Tração"],["impacto","Impacto"],["estanqueidade","Estanqueidade"]].map(([k,l]) => (
              <div key={k} className="flex items-center justify-between bg-slate-50 rounded px-2 py-1">
                <span className="text-xs">{l}</span><StatusBadge val={i.mechanical?.[k]} />
              </div>
            ))}
          </div>
        </div>

        {/* Weight + Chemical */}
        <div className="grid grid-cols-2 gap-3">
          <div className="border rounded-lg p-3">
            <p className="font-bold text-sm mb-2">4. Peso</p>
            <p className="text-sm">Peso: <span className="font-mono font-bold">{i.weight?.valor ? `${i.weight.valor} g` : "N/A"}</span></p>
            <p className="text-sm">Uniforme: <StatusBadge val={i.weight?.uniforme} /></p>
          </div>
          <div className="border rounded-lg p-3">
            <p className="font-bold text-sm mb-2">5. Químicas</p>
            <p className="text-sm">MFI: <span className="font-mono font-bold">{i.chemical?.fluidez ? `${i.chemical.fluidez} g/10min` : "N/A"}</span></p>
          </div>
        </div>

        {i.observacoes && (
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="font-bold text-sm mb-1">Observações</p>
            <p className="text-sm text-slate-600">{i.observacoes}</p>
          </div>
        )}

        <div className="flex justify-between items-center pt-2 border-t text-xs text-slate-400">
          <span>Registado: {new Date(i.created_at).toLocaleString("pt-PT")}</span>
          <Button onClick={handlePrint} className="gap-2 bg-indigo-600 hover:bg-indigo-700">
            <Printer className="h-4 w-4" /> Exportar PDF
          </Button>
        </div>
      </div>
    </DialogContent>
  );
}

export default function QualityInspections() {
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const fetchInspections = async () => {
    try {
      const r = await axios.get(`${API}/quality-inspections`);
      setInspections(r.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchInspections(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar esta inspeção?")) return;
    try {
      await axios.delete(`${API}/quality-inspections/${id}`);
      toast.success("Inspeção eliminada");
      fetchInspections();
    } catch (e) { toast.error("Erro"); }
  };

  const filtered = inspections.filter(i =>
    (i.order_code || "").toLowerCase().includes(search.toLowerCase()) ||
    (i.machine_code || "").toLowerCase().includes(search.toLowerCase()) ||
    (i.part_code || "").toLowerCase().includes(search.toLowerCase()) ||
    (i.inspector_name || "").toLowerCase().includes(search.toLowerCase())
  );

  const stats = {
    total: inspections.length,
    conforme: inspections.filter(i => i.resultado_global === "conforme").length,
    nao_conforme: inspections.filter(i => i.resultado_global === "nao_conforme").length,
    condicional: inspections.filter(i => i.resultado_global === "condicional").length,
  };

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="quality-inspections-page">
      <div>
        <h1 className="font-chivo text-3xl font-bold text-foreground flex items-center gap-3">
          <ClipboardCheck className="h-7 w-7" />
          Técnico Qualidade
        </h1>
        <p className="text-muted-foreground mt-1">Registos de inspeções de qualidade</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card><CardContent className="pt-4 text-center"><p className="text-xs text-muted-foreground">Total Inspeções</p><p className="text-3xl font-bold">{stats.total}</p></CardContent></Card>
        <Card><CardContent className="pt-4 text-center"><p className="text-xs text-green-600">Conformes</p><p className="text-3xl font-bold text-green-600">{stats.conforme}</p></CardContent></Card>
        <Card><CardContent className="pt-4 text-center"><p className="text-xs text-red-600">Não Conformes</p><p className="text-3xl font-bold text-red-600">{stats.nao_conforme}</p></CardContent></Card>
        <Card><CardContent className="pt-4 text-center"><p className="text-xs text-amber-600">Condicionais</p><p className="text-3xl font-bold text-amber-600">{stats.condicional}</p></CardContent></Card>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Pesquisar por ordem, máquina, peça..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
      </div>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><ClipboardCheck className="h-4 w-4" /> Inspeções Registadas</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data</TableHead>
                <TableHead>Ordem</TableHead>
                <TableHead>Máquina</TableHead>
                <TableHead>Peça</TableHead>
                <TableHead>Inspector</TableHead>
                <TableHead>Resultado</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Nenhuma inspeção registada</TableCell></TableRow>
              ) : (
                filtered.map(i => {
                  const ResultIcon = resultIcons[i.resultado_global] || CheckCircle2;
                  return (
                    <TableRow key={i.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelected(i)}>
                      <TableCell className="font-mono text-sm">{new Date(i.created_at).toLocaleString("pt-PT", {day:"2-digit",month:"2-digit",year:"2-digit",hour:"2-digit",minute:"2-digit"})}</TableCell>
                      <TableCell className="font-mono font-bold">{i.order_code}</TableCell>
                      <TableCell>{i.machine_code}</TableCell>
                      <TableCell>{i.part_code} <span className="text-muted-foreground text-xs">{i.part_description}</span></TableCell>
                      <TableCell>{i.inspector_name}</TableCell>
                      <TableCell>
                        <Badge className={`${resultColors[i.resultado_global]} text-white gap-1`}>
                          <ResultIcon className="h-3 w-3" />
                          {resultLabels[i.resultado_global]}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1" onClick={e => e.stopPropagation()}>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setSelected(i)}><Eye className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => handleDelete(i.id)}><Trash2 className="h-4 w-4" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        {selected && <InspectionDetail inspection={selected} onClose={() => setSelected(null)} />}
      </Dialog>
    </div>
  );
}
